import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Warning, TrendDown, PaperPlaneTilt, ShieldCheck } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const RISK = {
  "élevé": { label: "Risque élevé", cls: "bg-accent text-accent-foreground", border: "border-l-accent" },
  moyen: { label: "Risque moyen", cls: "bg-amber-500 text-white", border: "border-l-amber-500" },
  faible: { label: "Risque faible", cls: "bg-emerald-600 text-white", border: "border-l-emerald-600" },
};

export default function Decrochage() {
  const [data, setData] = useState(null);
  const [notif, setNotif] = useState(null);
  const [msg, setMsg] = useState("");
  const load = () => api.get("/dropout/risk").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNotif = (s) => { setMsg(`Bonjour ${s.name.split(" ")[0]}, nous avons remarqué une baisse d'activité. N'hésitez pas à revenir vers vos cours, et contactez-nous si vous avez besoin d'aide.`); setNotif(s); };
  const sendNotif = async () => {
    try { await api.post("/dropout/notify", { student_id: notif.student_id, message: msg }); toast.success("Message envoyé à l'étudiant"); setNotif(null); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="text-muted-foreground">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="dropout-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Analytique & suivi</p>
        <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tight"><TrendDown size={28} weight="duotone" className="text-accent" /> Détection du décrochage</h1>
        <p className="text-sm text-muted-foreground">Repérez les étudiants à risque et intervenez rapidement.</p>
      </div>

      <div className="grid gap-5 sm:grid-cols-3">
        <div className="rounded-xl border border-l-4 border-l-accent border-border bg-card p-5">
          <div className="flex items-center gap-2 text-accent"><Warning size={22} weight="duotone" /><span className="text-sm font-medium text-muted-foreground">Risque élevé</span></div>
          <p className="mt-2 font-display text-3xl font-extrabold">{data.summary.eleve}</p>
        </div>
        <div className="rounded-xl border border-l-4 border-l-amber-500 border-border bg-card p-5">
          <div className="flex items-center gap-2 text-amber-600"><TrendDown size={22} weight="duotone" /><span className="text-sm font-medium text-muted-foreground">Risque moyen</span></div>
          <p className="mt-2 font-display text-3xl font-extrabold">{data.summary.moyen}</p>
        </div>
        <div className="rounded-xl border border-l-4 border-l-emerald-600 border-border bg-card p-5">
          <div className="flex items-center gap-2 text-emerald-600"><ShieldCheck size={22} weight="duotone" /><span className="text-sm font-medium text-muted-foreground">Risque faible</span></div>
          <p className="mt-2 font-display text-3xl font-extrabold">{data.summary.faible}</p>
        </div>
      </div>

      {data.students.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-16 text-center text-muted-foreground">Aucun étudiant à analyser.</div>
      ) : (
        <div className="space-y-3">
          {data.students.map((s) => {
            const r = RISK[s.risk_level] || RISK.faible;
            return (
              <div key={s.student_id} className={`rounded-xl border border-l-4 ${r.border} border-border bg-card p-5`} data-testid={`risk-${s.student_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{s.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>
                    <div>
                      <p className="font-semibold">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.email}</p>
                    </div>
                  </div>
                  <Badge className={`${r.cls} hover:${r.cls}`}>{r.label}</Badge>
                </div>
                <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span>Moyenne : <b className="text-foreground">{s.average != null ? `${s.average}/20` : "—"}</b></span>
                  <span>Participation : <b className="text-foreground">{s.engagement != null ? `${Math.round(s.engagement * 100)}%` : "—"}</b></span>
                  <span>Devoirs : <b className="text-foreground">{s.submissions}/{s.assignments}</b></span>
                  <span>Quiz : <b className="text-foreground">{s.quiz_attempts}/{s.quizzes}</b></span>
                  {s.inactive_days != null && <span>Inactif : <b className="text-foreground">{s.inactive_days} j</b></span>}
                </div>
                {s.reasons.length > 0 && (
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {s.reasons.map((rs, i) => <li key={i} className="rounded-full bg-secondary px-3 py-1 text-xs">{rs}</li>)}
                  </ul>
                )}
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openNotif(s)} data-testid={`alert-${s.student_id}`}><PaperPlaneTilt size={14} className="mr-1" /> Alerter / Encourager</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!notif} onOpenChange={(o) => !o && setNotif(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Message à {notif?.name}</DialogTitle></DialogHeader>
          <Textarea rows={5} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="alert-message" />
          <DialogFooter><Button onClick={sendNotif} className="bg-primary hover:bg-primary/90" data-testid="alert-send">Envoyer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
